// Supervisor events (#/events): open events grouped by state, owner, age and severity, Take, and a
// closed-events view. Rows open the event in the console (read-only unless you own it).

import { useCallback, useEffect, useState } from 'react';
import { api, errorText, isConflict, type EventState, type EventSummary } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { Empty, ErrorBox, InlineError, Loading } from '../components/ui.tsx';
import { AuditDrawer } from '../console/EventAudit.tsx';
import { AiStatePill, VerdictChip } from '../console/Agent.tsx';
import { stateHint, stateLabel } from '../console/ai.ts';
import { Since } from '../console/Ticks.tsx';
import { camShort, human, SEV_CLS, SEV_LABEL, SEV_RANK } from '../console/util.ts';
import { useLoad } from '../hooks.ts';
import { href, navigate } from '../router.ts';
import { useSession } from '../session.tsx';
import { useRefreshOn } from '../stream.tsx';
import { fmtDateTime } from '../time.ts';

const STATE_ORDER: EventState[] = ['active', 'offered', 'queued', 'settling', 'agent_working', 'parked', 'transferred', 'dispositioned', 'closed'];
const PAGE = 100;

function Row({ e, onTake, onAudit, taking, meId }: { e: EventSummary; onTake: (e: EventSummary) => void; onAudit: (e: EventSummary) => void; taking: boolean; meId: string }) {
  const open = () => navigate(href.console(e.id));
  // Phase 3 follow-up: Take also works on agent_working / settling (overrides the AI).
  const canTake = e.state === 'queued' || e.state === 'agent_working' || e.state === 'settling' || (e.state === 'active' && e.owner?.id !== meId);
  const closed = e.state === 'closed' || e.state === 'dispositioned';
  return (
    <tr className={`click s-${SEV_CLS[e.severity]}`} onClick={open} onKeyDown={(k) => k.key === 'Enter' && open()} tabIndex={0}>
      <td className="sevcell">
        <span className="sev">{SEV_LABEL[e.severity]}</span>
      </td>
      <td>
        <a href={href.console(e.id)} className="mono" onClick={(x) => x.stopPropagation()}>
          {e.displayNo}
        </a>
        {e.commissioning && <span className="pill warn tiny-pill">commissioning</span>}
      </td>
      <td>
        <span className="strong">{e.siteName}</span>
        <div className="note">{e.companyName ?? ''}</div>
      </td>
      <td>
        {human(e.primaryAnalytic)}
        <div className="note">{camShort(e.primaryCameraName)}</div>
      </td>
      <td className="nowrap">
        {e.ai?.verdict && <VerdictChip ai={e.ai} size="sm" />}
        <AiStatePill state={e.state} className={e.ai?.verdict ? 'tiny-pill' : ''} />
        {e.agentPaused && <span className="pill tiny-pill">agent paused</span>}
        {!e.ai?.verdict && !e.agentPaused && e.state !== 'agent_working' && e.state !== 'settling' && <span className="dim">—</span>}
      </td>
      <td className="mono num">{e.alarmCount}</td>
      <td>{e.owner ? e.owner.name : <span className="dim">—</span>}</td>
      <td className="mono nowrap">
        {closed ? <span className="dim">{fmtDateTime(e.lastAlarmAt, e.siteTimeZone)}</span> : <Since from={e.firstAlarmAt} lateAt={e.state === 'queued' ? e.slaDueAt : null} className="age" />}
      </td>
      <td className="nowrap">
        {!closed && canTake && (
          <button
            type="button"
            className="btn sm"
            disabled={taking}
            onClick={(x) => {
              x.stopPropagation();
              onTake(e);
            }}
          >
            <Icon name="users" />
            Take
          </button>
        )}{' '}
        <a className="btn sm" href={href.console(e.id)} onClick={(x) => x.stopPropagation()} title="Open in the console (read-only unless it is yours)">
          <Icon name="eye" />
          Open
        </a>{' '}
        <button
          type="button"
          className="btn sm"
          title="Everything recorded on this event: calls, recordings, video watch time"
          onClick={(x) => {
            x.stopPropagation();
            onAudit(e);
          }}
        >
          <Icon name="shield" />
          Audit
        </button>
      </td>
    </tr>
  );
}

export function EventsScreen({ state }: { state: 'open' | 'closed' | 'all' }) {
  const s = useSession();
  const [pages, setPages] = useState<EventSummary[][]>([]);
  const load = useLoad((signal) => api.events({ state, limit: PAGE }, signal), [state]);
  useRefreshOn(['event', 'queue'], () => {
    if (state !== 'closed') load.reload();
  }, 600);
  const [takingId, setTakingId] = useState<string | null>(null);
  const [takeErr, setTakeErr] = useState<unknown>(null);
  const [auditFor, setAuditFor] = useState<EventSummary | null>(null);
  const closeAudit = useCallback(() => setAuditFor(null), []);
  useEffect(() => setPages([]), [state]);

  const rows = [...(load.data ?? []), ...pages.flat()];
  const lastRow = rows[rows.length - 1];
  const canMore = (load.data?.length ?? 0) >= PAGE && (pages.length === 0 || (pages[pages.length - 1]?.length ?? 0) >= PAGE);

  const take = async (e: EventSummary) => {
    setTakingId(e.id);
    setTakeErr(null);
    try {
      const d = await api.take(e.id, e.version);
      navigate(href.console(d.id));
    } catch (err) {
      setTakeErr(isConflict(err) ? new Error(`${e.displayNo} changed before you took it: ${errorText(err)}. The list is refreshed; try again.`) : err);
      load.reload();
    } finally {
      setTakingId(null);
    }
  };

  const loadMore = async () => {
    if (!lastRow) return;
    try {
      // `before` pages by the oldest row's firstAlarmAt (the doc does not name the cursor field).
      const next = await api.events({ state, limit: PAGE, before: lastRow.firstAlarmAt });
      setPages((p) => [...p, next]);
    } catch (err) {
      setTakeErr(err);
    }
  };

  const groups = new Map<EventState, EventSummary[]>();
  for (const e of rows) {
    const l = groups.get(e.state) ?? [];
    l.push(e);
    groups.set(e.state, l);
  }
  const ordered = STATE_ORDER.filter((st) => groups.has(st));
  const counts = { crit: 0, high: 0, queued: 0, active: 0, ai: 0, settling: 0 };
  for (const e of load.data ?? []) {
    if (e.severity === 'critical') counts.crit++;
    if (e.severity === 'high') counts.high++;
    if (e.state === 'queued') counts.queued++;
    if (e.state === 'active') counts.active++;
    if (e.state === 'agent_working') counts.ai++;
    if (e.state === 'settling') counts.settling++;
  }

  return (
    <div className="page">
      <div className="page-h">
        <div>
          <h2>Events</h2>
          <p>Open events by state with owner, age and severity. Take moves an event to you; Open shows it in the console.</p>
        </div>
        <span className="sp" />
        <div className="subnav" role="tablist" aria-label="Event filter">
          {(['open', 'closed', 'all'] as const).map((k) => (
            <a key={k} href={href.events(k)} className={state === k ? 'on' : ''} role="tab" aria-selected={state === k} onClick={() => setPages([])}>
              {k === 'open' ? 'Open' : k === 'closed' ? 'Closed' : 'All'}
            </a>
          ))}
        </div>
      </div>
      {state === 'open' && load.data && (
        <div className="tiles">
          <div className="tilek">
            <span className="lbl">Queued</span>
            <b>{counts.queued}</b>
            <small>waiting for an operator</small>
          </div>
          <div className="tilek">
            <span className="lbl">Active</span>
            <b>{counts.active}</b>
            <small>being handled</small>
          </div>
          {s.config.features.agent && (
            <div className="tilek aik">
              <span className="lbl">With the AI</span>
              <b>
                {counts.ai} · {counts.settling}
              </b>
              <small>AI triaging · auto-resolved, closing</small>
            </div>
          )}
          <div className="tilek">
            <span className="lbl">Critical / high</span>
            <b>
              <span style={{ color: 'var(--crit)' }}>{counts.crit}</span> / <span style={{ color: 'var(--high)' }}>{counts.high}</span>
            </b>
            <small>open events</small>
          </div>
        </div>
      )}
      {takeErr ? <InlineError error={takeErr} /> : null}
      <div className="card2 flush">
        {load.error && !load.data ? (
          <ErrorBox error={load.error} onRetry={load.reload} title="Could not load events" />
        ) : !load.data ? (
          <Loading label="Loading events…" />
        ) : rows.length === 0 ? (
          <Empty>{state === 'closed' ? 'No closed events yet.' : 'No open events. The queue is clear.'}</Empty>
        ) : (
          <div className="tw">
            <table className="evt">
              <thead>
                <tr>
                  <th scope="col">Sev</th>
                  <th scope="col">Event</th>
                  <th scope="col">Site</th>
                  <th scope="col">Analytic · camera</th>
                  <th scope="col" title="AI verdict (Level 1+ sites)">AI</th>
                  <th scope="col" className="num">Alarms</th>
                  <th scope="col">Owner</th>
                  <th scope="col">{state === 'closed' ? 'Last alarm' : 'Age'}</th>
                  <th scope="col" />
                </tr>
              </thead>
              {ordered.map((st) => (
                <tbody key={st}>
                  <tr className="grouprow">
                    <th colSpan={9} scope="rowgroup" title={stateHint(st) ?? undefined}>
                      {stateLabel(st)} · {groups.get(st)?.length ?? 0}
                    </th>
                  </tr>
                  {(groups.get(st) ?? [])
                    .slice()
                    .sort((a, b) => SEV_RANK[a.severity] - SEV_RANK[b.severity] || Date.parse(a.firstAlarmAt) - Date.parse(b.firstAlarmAt))
                    .map((e) => (
                      <Row key={e.id} e={e} onTake={(x) => void take(x)} onAudit={setAuditFor} taking={takingId === e.id} meId={s.me.userId} />
                    ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
        {canMore && (
          <div className="tfoot">
            <button type="button" className="btn" onClick={() => void loadMore()}>
              Load older
            </button>
          </div>
        )}
      </div>
      {auditFor && <AuditDrawer eventId={auditFor.id} title={`${auditFor.displayNo} · ${auditFor.siteName}`} tz={auditFor.siteTimeZone} onClose={closeAudit} />}
    </div>
  );
}
